import autoBindMethods from 'class-autobind-decorator';
import { computed, observable } from 'mobx';
import { get, uniqBy } from 'lodash';

import Client from '../clients/Client';
import { ILoanAccountActivity } from '../interfaces';

interface ILoanAccountActivityApiParams {
  account?: string;
  page_size?: number;
  type?: string;
}

interface IApiLoanAccountActivities {
  count: number;
  next?: string;
  previous?: string;
  results: ILoanAccountActivity[];
}

const ACTIVITIES_PAGE_SIZE = 100;

// c.f. ActivityStore. Oldest first, since the list reverses before rendering.
const ACTIVITIES_SORT = (a: ILoanAccountActivity, b: ILoanAccountActivity) => {
  // Actual sort order
  if (a.created_at < b.created_at) { return -1; }
  if (a.created_at > b.created_at) { return 1; }

  // Determinism
  if (a.id < b.id) { return -1; }
  if (a.id > b.id) { return 1; }

  // istanbul ignore next
  return 0;
};

/**
 * The loan account equivalent of ActivityStore. Kept separate rather than generalizing that one:
 * loan account activities are a single model with their own field names.
 */
@autoBindMethods
class LoanAccountActivityStore {
  private client: Client;

  // Read during render by the type filter, so it has to be observable in its own right: changing the
  // filter and re-reading the feed are two ticks apart.
  @observable private params: ILoanAccountActivityApiParams;

  // hasMore is read during render, so the response has to be observable in its own right rather
  // than relying on loading and rawActivities changing in the same tick.
  @observable private response?: IApiLoanAccountActivities;
  @observable private rawActivities: ILoanAccountActivity[] = [];

  @observable public loading = true;
  @observable public loadingMore = false;

  constructor (client: Client, params: ILoanAccountActivityApiParams) {
    this.client = client;
    this.params = { page_size: ACTIVITIES_PAGE_SIZE, ...params };
  }

  @computed
  get activities () {
    return uniqBy(this.rawActivities, 'id').sort(ACTIVITIES_SORT);
  }

  @computed
  get hasMore () {
    return !!get(this.response, 'next');
  }

  @computed
  get typeFilter () {
    return this.params.type || null;
  }

  /**
   * Transport has already toasted the failure, and every caller is a lifecycle hook or an onClick
   * with nowhere to hand a rejection on to, so failures are swallowed here rather than rethrown.
   */
  private logFailure (message: string, error: any) {
    // tslint:disable-next-line no-console
    console.error(message, error);
  }

  private async load () {
    // Assigned only once the request comes back, so that a failure leaves the feed showing what it
    // already had — entries and, with them, whether there are older pages left to ask for.
    const response = await this.client.list({ params: this.params }) as IApiLoanAccountActivities;

    this.response = response;
    this.rawActivities = get(response, 'results', []);
  }

  // The account is fixed at construction: this store only ever reads one account's feed.
  public async loadInitial () {
    this.loading = true;

    try {
      await this.load();
    }
    catch (error) {
      // The feed shows itself as empty rather than leaving the account page under a spinner forever
      this.logFailure('Error loading the account activity feed:', error);
    }
    finally {
      this.loading = false;
    }
  }

  /**
   * Called when something elsewhere on the page has written to this account's feed. Deliberately
   * does not raise `loading`: the entries already on screen stay put while the reload is in flight
   * rather than the feed blinking back to its loader.
   */
  public async refresh () {
    try {
      await this.load();
    }
    catch (error) {
      this.logFailure('Error refreshing the account activity feed:', error);
    }
  }

  /**
   * Filtering is the API's rather than a pass over what has been fetched: the feed pages, so a type
   * whose entries are all older than the first page would come back empty from a local filter.
   * c.f. ActivityStore, which filters the case feed the same way.
   */
  public async setTypeFilter (type: null | string) {
    this.setTypeParam(type);

    await this.loadInitial();
  }

  private setTypeParam (type: null | string) {
    this.params = { ...this.params, type: type || undefined };
  }

  /**
   * Called once something on the page has written an entry of `type` to the feed. Under a filter for
   * another type that entry would not come back, leaving the feed looking as though nothing was
   * saved, so the filter comes off rather than the entry going missing. c.f. ActivityStore.loadNew.
   */
  public async refreshForType (type: string) {
    // Dropping the filter through refresh rather than setTypeFilter: the entries on screen stay put
    // while the reload is in flight, as they do when the filter already matched.
    if (this.typeFilter && this.typeFilter !== type) {
      this.setTypeParam(null);
    }

    await this.refresh();
  }

  public async loadMore () {
    // istanbul ignore next
    if (!this.response || !this.response.next) {
      return;
    }

    this.loadingMore = true;

    try {
      const response = await this.client.loadMore(this.response.next);
      this.response = response;
      this.rawActivities = this.rawActivities.concat(response.results);
    }
    catch (error) {
      // The page the user asked for is dropped, so Load More stays offering it another try
      this.logFailure('Error loading more account activity:', error);
    }
    finally {
      this.loadingMore = false;
    }
  }
}

export default LoanAccountActivityStore;
